import {
	noopSliderTransform,
	Slider,
	sliderBackgrounds,
	SliderFocusProvider,
	useSliderFocus,
} from "./ui/Slider"
import { useEditor } from "@/utils/useEditor"
import { Curves } from "./Curves"
import { HSL } from "./HSL/HSL"
import { Grading } from "./Grading"
import {
	adjustmentsHistoryAtom,
	currentWhiteBalanceSettingAtom,
	saveCurrentAdjustmentState,
	startSavingAdjustmentState,
} from "@/store/store-history"
import { ControlGroup } from "./ControlGroup"
import { Button } from "./ui/Button"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { currentPhotoAtom, getPhotoMetadata } from "@/store/photo/photos-state"
import { getOrExtractImageInfoWithLock } from "@/store/processing/image-info"
import { constrainCrop } from "@/components/Crop/crop-interaction-handlers"
import { vec2 } from "gl-matrix"
import { AdjustmentGroups, getDefaultAdjustments } from "@/core/adjustments"
import { AdjustmentKey, Adjustments, ColorProfileName } from "@/core/types"
import { ControlsToolBar, ControlToolsPanel, ToolPanelProps } from "@/components/ControlsToolBar"
import { ScrollBar } from "@/components/ui/ScrollBar"
import { Switch } from "./ui/Switch"
import { selectedMaskGroupAtom } from "../store/editor/masks-state"
import { Suspense, useEffect, useRef } from "react"
import { usePrevious } from "../utils/usePrevious"
import { Histogram } from "./Histogram"
import { ColorPickerPipette } from "./ColorPicker/ColorPickerPipette"
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
} from "@/components/ui/Select"
import { WbOption } from "@/store/undoable-state"
import { ColorSampleResult } from "@/core/color-sampler"
import {
	activeEditingToolPanelAtom,
	leftExpandedPanelAtom,
	showingHealingPanelAtom,
	showingPeoplePanelAtom,
	showToast,
} from "@/store/ui-views-state"
import { RGBToWhiteBalance } from "@/core/utils/temperature"
import { HSLPointColor } from "./HSL/HSLPointColor"
import { isColorProfileMonochrome } from "@/core/utils/camera-raw-settings"
import { openBasicEditView } from "@/store/photo/photos-view-state"
import { useShortcuts } from "@/utils/useShortcuts"
import {
	queueApplyBasicAutoAdjustments,
	saveColorProfileHistory,
} from "@/store/processing/image-processing"
import { currentPresetAdjustmentsAtom } from "@/store/store-presets"
import { isManualModeAtom } from "@/store/project/projects-state"
import { PhotoRecord } from "@/lib/catalog"
import { DualSlider } from "./ui/DualSlider"
import { RGBToHSL } from "@/core/utils/color-conversion"
import { clamp } from "@/utils/math"
import {
	isCurrentPhotoSourceMonochromeAtom,
	isPhotoLoadedIntoEditorAtom,
} from "@/store/editor/photo-loading"
import { actionShortcuts } from "@/shortcuts/shortcuts"
import { ContentTooltipWrapper } from "./ui/ContentTooltip"
import { openFilesWithPicker } from "@/utils/file"
import { importUserLutFromCUBEFile } from "@/store/store-presets"
import { ColorMatchPanel } from "./ColorMatchPanel"
import { useTranslation } from "react-i18next"

async function getAutoVerticalAdjustments(
	photo: PhotoRecord,
	adjustments: Adjustments,
	orientedSize: vec2,
	initialOrientation: number,
) {
	const imageInfo = await getOrExtractImageInfoWithLock(photo)
	const { crop: _crop, ...autoPerspective } = imageInfo?.features.autoCorrection ?? {}

	adjustments = { ...adjustments, ...autoPerspective }
	const newCrop = constrainCrop(adjustments, orientedSize, initialOrientation)

	return {
		...autoPerspective,
		crop: newCrop,
	}
}

export const Controls = (props: ToolPanelProps) => {
	const activePanel = useAtomValue(activeEditingToolPanelAtom)

	return (
		<SliderFocusProvider enabled={activePanel === "edit"}>
			<ControlsContent {...props} />
		</SliderFocusProvider>
	)
}

const useScrollOnStateChange = () => {
	// scroll to top when mask group is changed
	const [selectedMaskGroup] = useAtom(selectedMaskGroupAtom)
	const previousSelectedMaskGroup = usePrevious(selectedMaskGroup)
	const ref = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const selectedId = selectedMaskGroup?.properties.id
		const previousId = previousSelectedMaskGroup?.properties.id

		if (selectedId !== previousId) {
			ref.current?.scrollTo({ top: 0, behavior: "smooth" })
		}
	}, [selectedMaskGroup, previousSelectedMaskGroup])

	// scroll to top when healing panel is opened
	const isHealingActive = useAtomValue(showingHealingPanelAtom)
	const isPeopleActive = useAtomValue(showingPeoplePanelAtom)

	useEffect(() => {
		if (isHealingActive || isPeopleActive) {
			ref.current?.scrollTo({ top: 0, behavior: "smooth" })
		}
	}, [isHealingActive, isPeopleActive])

	return { ref }
}

const ControlsContent = ({ adjustments, disabled }: ToolPanelProps) => {
	const { t } = useTranslation("components")
	const editor = useEditor()
	const currentPhoto = useAtomValue(currentPhotoAtom)
	const clearCurrentPresetAdjustments = useSetAtom(currentPresetAdjustmentsAtom)
	const setLeftExpandedPanel = useSetAtom(leftExpandedPanelAtom)
	const adjustmentsHistory = useAtomValue(adjustmentsHistoryAtom)
	const selectedGroup = useAtomValue(selectedMaskGroupAtom)
	const isManualMode = useAtomValue(isManualModeAtom)
	const isPhotoLoaded = useAtomValue(isPhotoLoadedIntoEditorAtom)
	const isSourceMonochrome = useAtomValue(isCurrentPhotoSourceMonochromeAtom)
	const activePanel = useAtomValue(activeEditingToolPanelAtom)
	const sliderFocus = useSliderFocus()

	const areAdjustmentsPending =
		!isPhotoLoaded || adjustmentsHistory[0]?.photoId !== currentPhoto?.id
	const isMonochromeProfile = isColorProfileMonochrome(adjustments.colorProfile)
	const disablesChromaControls = isSourceMonochrome || isMonochromeProfile
	const focusedSliderKey = sliderFocus?.focusedKey ?? null
	const setFocusedSliderKey = sliderFocus?.setFocusedKey

	const [currentWhiteBalanceSetting, setCurrentWhiteBalanceSetting] = useAtom(
		currentWhiteBalanceSettingAtom,
	)

	const { ref } = useScrollOnStateChange()

	useEffect(() => {
		if (activePanel !== "edit") {
			setFocusedSliderKey?.(null)
			return
		}

		if (!focusedSliderKey) {
			setFocusedSliderKey?.("exposure")
		}
	}, [activePanel, focusedSliderKey, setFocusedSliderKey])

	const nextAdjustment = () => {
		if (selectedGroup) {
			return
		}

		const nextIndex =
			currentAdjustmentIndex < allBasicAdjustments.length - 1 ? currentAdjustmentIndex + 1 : 0
		const nextKey = allBasicAdjustments[nextIndex]
		if (nextKey) {
			sliderFocus?.setFocusedKey(nextKey)
		}
	}

	const prevAdjustment = () => {
		if (selectedGroup) {
			return
		}

		const nextIndex =
			currentAdjustmentIndex > 0 ? currentAdjustmentIndex - 1 : allBasicAdjustments.length - 1
		const nextKey = allBasicAdjustments[nextIndex]
		if (nextKey) {
			sliderFocus?.setFocusedKey(nextKey)
		}
	}

	// Accept , . < > on QWERTY keyboards, and < > on AZERTY keyboards.
	useShortcuts({
		keys: [actionShortcuts.prevAdjustment, "<", "shift!<"],
		action: prevAdjustment,
		trackingLabel: "prev_adjustment",
		delimiter: "|",
		splitKey: "!",
		ignoreEventWhen: (event) => event.key === "," && event.code !== "Comma",
		disabled: activePanel === "crop",
	})

	useShortcuts({
		keys: [actionShortcuts.nextAdjustment, "shift!>"],
		action: nextAdjustment,
		trackingLabel: "next_adjustment",
		splitKey: "!",
		sequenceSplitKey: "|",
		ignoreEventWhen: (event) => event.key === "." && event.code !== "Period",
		disabled: activePanel === "crop",
	})

	useShortcuts({
		keys: [actionShortcuts.openBasicEdit],
		action: () => {
			openBasicEditView()
			ref.current?.scrollTo({ top: 0, behavior: "smooth" })
		},
		trackingLabel: "open_basic_edit",
	})

	const allBasicAdjustments = AdjustmentGroups.basic as AdjustmentKey[]
	const currentAdjustmentIndex =
		sliderFocus?.focusedKey &&
		allBasicAdjustments.includes(sliderFocus.focusedKey as AdjustmentKey)
			? allBasicAdjustments.indexOf(sliderFocus.focusedKey as AdjustmentKey)
			: -1

	const handleUpdate = (name: keyof Adjustments, val: number | [number, number]) => {
		if (name === "userLutStrength") {
			clearCurrentPresetAdjustments(null)
		}

		editor.setAdjustments({ [name]: val })

		startSavingAdjustmentState()
	}

	const handleUpdateEnded = (name: string, val?: number | string) => {
		saveCurrentAdjustmentState({
			message: name,
			changedValue: val?.toString(),
		})
	}

	const handleTransformUpdate = (name: keyof Adjustments, val: number) => {
		const crop = constrainCrop(
			{ ...adjustments, [name]: val },
			editor.imageSize,
			editor.initialOrientation,
		)

		editor.setAdjustments({ [name]: val, crop })
	}

	async function autoVertical() {
		if (currentPhoto) {
			const verticalAdjustments = await getAutoVerticalAdjustments(
				currentPhoto,
				adjustments,
				editor.imageSize,
				editor.initialOrientation,
			)
			editor.setAdjustments(verticalAdjustments)
		}

		saveCurrentAdjustmentState({
			message: "Auto Vertical",
		})
	}

	const hueInFringeRange = (hue: number) => {
		// Check if the hue is within the purple or green range
		if (
			(hue >= 200 / 360 && hue <= 350 / 360) || // purple
			(hue >= 60 / 360 && hue <= 180 / 360) // green
		) {
			return true
		}

		return false
	}

	const handleFringeColor = (result: ColorSampleResult) => {
		const hsl = RGBToHSL(result.color)
		const hue = hsl[0] / 360

		if (!hueInFringeRange(hue)) {
			showToast(t("controls.toastFringeColorInvalid"))
			return false
		}

		if (hue >= 60 / 360 && hue <= 180 / 360) {
			const start = clamp(hue - 0.05, 60 / 360, 0.5)
			const end = clamp(hue + 0.05, 60 / 360, 0.5)
			handleUpdate("defringeGreenHueRange", [start, end])
			handleUpdate("defringeGreenAmount", 0.5)
			handleUpdateEnded("DefringeGreen")
		} else {
			const start = clamp(hue - 0.05, 0.5, 1)
			const end = clamp(hue + 0.05, 0.5, 1)
			handleUpdate("defringePurpleHueRange", [start, end])
			handleUpdate("defringePurpleAmount", 0.5)
			handleUpdateEnded("DefringePurple")
		}

		return true
	}

	const getLensInfo = () => {
		const metadata = getPhotoMetadata(currentPhoto?.id ?? "")

		const lensModelName = metadata?.lensModel?.trim() ?? ""
		const lensMakerName = metadata?.lensMake?.trim() || metadata?.cameraMake?.trim() || ""

		const hasLensModel = lensModelName.length > 0

		const name = hasLensModel
			? [lensMakerName, lensModelName].filter(Boolean).join(" ")
			: t("controls.unknownLens")

		return {
			hasLensModel,
			name,
		}
	}

	const renderProfileLensCorrection = () => {
		const isDistortionCorrectable = editor.renderer.profileManager.isLensDistortionCorrectable()
		const isVignetteCorrectable = editor.renderer.profileManager.isLensVignetteCorrectable()

		const canCorrect = isDistortionCorrectable || isVignetteCorrectable
		const isEnabled = canCorrect && (adjustments.enableLensCorrections ?? true)

		const isChromaticAberrationCorrectable =
			editor.renderer.profileManager.isLensChromaticAberrationCorrectable()
		const lensInfo = getLensInfo()

		return (
			<>
				<div
					className={`rounded-md bg-base-700 p-2 text-gray-700 dark:text-gray-300 ${
						!isEnabled ? "opacity-50" : ""
					}`}
				>
					{lensInfo.name}
				</div>
				<div
					className={`flex flex-row items-center space-x-2 ${
						!isEnabled ? "text-gray-700/30 dark:text-gray-100/30" : ""
					}`}
				>
					{t("controls.correctLensDistortion")}
					<div className="flex-1"></div>
					<Switch
						trackingLabel="correct_lens_distortion"
						aria-label={t("controls.ariaToggleLensDistortionCorrection")}
						checked={
							isDistortionCorrectable ? adjustments.correctLensDistortion : false
						}
						disabled={!isEnabled || !isDistortionCorrectable}
						onCheckedChange={(checked) => {
							editor.setAdjustments({ correctLensDistortion: checked })
							handleUpdateEnded("Correct Lens Distortion", checked ? "on" : "off")
						}}
					/>
				</div>
				<div
					className={`flex flex-row items-center space-x-4 ${
						!isEnabled ? "text-gray-700/30 dark:text-gray-100/30" : ""
					}`}
				>
					{t("controls.correctLensVignette")}
					<div className="flex-1"></div>
					<Switch
						trackingLabel="correct_lens_vignette"
						aria-label={t("controls.ariaToggleLensVignetteCorrection")}
						checked={isVignetteCorrectable ? adjustments.correctLensVignette : false}
						disabled={!isEnabled || !isVignetteCorrectable}
						onCheckedChange={(checked) => {
							editor.setAdjustments({ correctLensVignette: checked })
							handleUpdateEnded("Correct Lens Vignette", checked ? "on" : "off")
						}}
					/>
				</div>
				<div
					className={`flex flex-row items-center space-x-4 ${
						!isEnabled ? "text-gray-700/30 dark:text-gray-100/30" : ""
					}`}
				>
					{t("controls.correctLensChromaticAberration")}
					<div className="flex-1"></div>
					<Switch
						trackingLabel="correct_lens_chromatic_aberration"
						aria-label={t("controls.ariaToggleLensChromaticAberrationCorrection")}
						checked={
							isChromaticAberrationCorrectable
								? adjustments.correctLensChromaticAberration
								: false
						}
						disabled={!isEnabled || !isChromaticAberrationCorrectable}
						onCheckedChange={(checked) => {
							editor.setAdjustments({ correctLensChromaticAberration: checked })
							handleUpdateEnded(
								"Correct Lens Chromatic Aberration",
								checked ? "on" : "off",
							)
						}}
					/>
				</div>
				<div>
					<Slider
						disabled={
							!isEnabled ||
							!isDistortionCorrectable ||
							!adjustments.correctLensDistortion
						}
						min={50}
						max={150}
						defaultValue={1.0}
						title={t("controls.sliderDistortion")}
						panelName={t("controls.panelLensCorrection")}
						focusKey="lensDistortionCorrectionFactor"
						keyboardStep={1}
						shiftKeyboardStep={5}
						value={adjustments["lensDistortionCorrectionFactor"]}
						onUpdate={(val) => handleUpdate("lensDistortionCorrectionFactor", val)}
						onUpdateEnded={(_, displayedValue) =>
							handleUpdateEnded("Lens Distortion Correction Factor", displayedValue)
						}
					/>
					<Slider
						disabled={
							!isEnabled || !isVignetteCorrectable || !adjustments.correctLensVignette
						}
						min={50}
						max={150}
						defaultValue={1.0}
						title={t("controls.sliderVignette")}
						panelName={t("controls.panelLensCorrection")}
						focusKey="lensVignetteCorrectionFactor"
						keyboardStep={1}
						shiftKeyboardStep={5}
						value={adjustments["lensVignetteCorrectionFactor"]}
						onUpdate={(val) => handleUpdate("lensVignetteCorrectionFactor", val)}
						onUpdateEnded={(_, displayedValue) =>
							handleUpdateEnded("Lens Vignette Correction Factor", displayedValue)
						}
					/>
				</div>
			</>
		)
	}

	const renderLensCorrection = () => {
		const isDistortionCorrectable = editor.renderer.profileManager.isLensDistortionCorrectable()
		const isVignetteCorrectable = editor.renderer.profileManager.isLensVignetteCorrectable()

		const canCorrect = isDistortionCorrectable || isVignetteCorrectable
		const isEnabled = canCorrect && (adjustments.enableLensCorrections ?? true)
		const lensInfo = getLensInfo()
		const tooltipContent = t("controls.tooltipLensDetectedNoCorrection")
		const shouldShowTooltip =
			!isDistortionCorrectable && !isVignetteCorrectable && lensInfo.hasLensModel
		/* View */

		return (
			<ControlGroup
				expandKey="lensCorrection"
				name={t("controls.panelLensCorrection")}
				disabled={disabled}
			>
				<div className="my-2 flex w-full flex-col space-y-2 text-xs">
					<ContentTooltipWrapper
						allowOpen={shouldShowTooltip}
						tooltipDelay={0}
						tooltip={tooltipContent}
						tooltipMaxWidth={280}
						tooltipProps={{
							side: "top",
							sideOffset: 8,
						}}
					>
						<div className="flex flex-row items-center space-x-4">
							{t("controls.enableProfileCorrections")}
							<div className="flex-1"></div>
							<Switch
								trackingLabel="enable_profile_corrections"
								aria-label={t("controls.ariaToggleLensProfileCorrections")}
								checked={isEnabled}
								disabled={!isDistortionCorrectable && !isVignetteCorrectable}
								onCheckedChange={(checked) => {
									editor.setAdjustments({ enableLensCorrections: checked })
									handleUpdateEnded(
										"Enable Profile Corrections",
										checked ? "on" : "off",
									)
								}}
							/>
						</div>
					</ContentTooltipWrapper>
					{renderProfileLensCorrection()}
				</div>
				<div className="my-2 border-b border-base-600/50 dark:border-base-900"></div>

				<div className="mt-2 flex w-full flex-col space-y-1 text-xs">
					<div className="relative flex">
						<div className="absolute">
							<ColorPickerPipette
								isForMask={false}
								className="ml-auto"
								onSelected={handleFringeColor}
								purpose="point-color"
							/>
						</div>
						<div className="my-2 ml-4 flex-1 text-center text-xs">
							{t("controls.defringe")}
						</div>
					</div>
					<Slider
						title={t("controls.sliderAmount")}
						panelName={t("controls.panelLensCorrection")}
						sectionName={t("controls.sectionPurpleDefringeAmount")}
						focusKey="defringePurpleAmount"
						min={0}
						max={20}
						background={sliderBackgrounds.defringePurpleAmount}
						value={adjustments["defringePurpleAmount"] / 4}
						keyboardStep={1}
						shiftKeyboardStep={10}
						hoverKeyboardStep={5}
						hoverShiftKeyboardStep={20}
						onUpdate={(val) => handleUpdate("defringePurpleAmount", val * 4)}
						onUpdateEnded={(_, displayedValue) =>
							handleUpdateEnded("DefringePurpleAmount", displayedValue)
						}
					/>
					<DualSlider
						title={t("controls.sliderPurpleHue")}
						background={sliderBackgrounds.defringePurpleHue}
						value={adjustments["defringePurpleHueRange"]}
						validRange={[194 / 360, 405 / 360]}
						resetValue={defaultAdjustments["defringePurpleHueRange"]}
						onUpdate={(value) => {
							handleUpdate("defringePurpleHueRange", value)
						}}
						onUpdateEnded={() => {
							handleUpdateEnded("DefringePurpleHueRange")
						}}
					/>
					<Slider
						title={t("controls.sliderAmount")}
						panelName={t("controls.panelLensCorrection")}
						sectionName={t("controls.sectionGreenDefringeAmount")}
						focusKey="defringeGreenAmount"
						min={0}
						max={20}
						background={sliderBackgrounds.defringeGreenAmount}
						value={adjustments["defringeGreenAmount"] / 4}
						keyboardStep={1}
						shiftKeyboardStep={10}
						hoverKeyboardStep={5}
						hoverShiftKeyboardStep={20}
						onUpdate={(val) => handleUpdate("defringeGreenAmount", val * 4)}
						onUpdateEnded={(_, displayedValue) =>
							handleUpdateEnded("DefringeGreenAmount", displayedValue)
						}
					/>
					<DualSlider
						title={t("controls.sliderGreenHue")}
						background={sliderBackgrounds.defringeGreenHue}
						value={adjustments["defringeGreenHueRange"]}
						validRange={[20 / 360, 220 / 360]}
						resetValue={defaultAdjustments["defringeGreenHueRange"]}
						onUpdate={(value) => {
							handleUpdate("defringeGreenHueRange", value)
						}}
						onUpdateEnded={() => {
							handleUpdateEnded("DefringeGreenHueRange")
						}}
					/>
				</div>
			</ControlGroup>
		)
	}

	const handleWBUpdate = (result: ColorSampleResult | WbOption) => {
		if (typeof result === "string") {
			if (result === "as-shot") {
				if (
					editor.adjustments.temperature !==
						editor.photo.whiteBalance.asShotTemperature ||
					editor.adjustments.tint !== editor.photo.whiteBalance.asShotTint
				) {
					editor.setAdjustments({
						temperature: editor.photo.whiteBalance.asShotTemperature,
						tint: editor.photo.whiteBalance.asShotTint,
					})

					saveCurrentAdjustmentState({
						message: "Set As-Shot White Balance",
					})

					setCurrentWhiteBalanceSetting("as-shot")
				}
			} else {
				const previousValue = adjustmentsHistory
					.slice(1)
					.reverse()
					.find(
						(entry) =>
							entry.adjustments.temperature !==
								editor.photo.whiteBalance.asShotTemperature ||
							entry.adjustments.tint !== editor.photo.whiteBalance.asShotTint,
					)

				if (previousValue) {
					editor.setAdjustments({
						temperature: previousValue.adjustments.temperature,
						tint: previousValue.adjustments.tint,
					})

					saveCurrentAdjustmentState({
						message: "Set Custom White Balance",
					})
				}

				setCurrentWhiteBalanceSetting("custom")
			}

			return true
		}

		if (
			result.colorRelative[0] === 1 &&
			result.colorRelative[1] === 1 &&
			result.colorRelative[2] === 1
		) {
			showToast(t("controls.toastWhiteBalanceTooLight"))

			return false
		}

		// Pure-black sample has no chromaticity information; the downstream
		// LMS chromatic-adaptation math would divide by zero (normalize_xyz).
		if (result.color[0] === 0 && result.color[1] === 0 && result.color[2] === 0) {
			showToast(t("controls.toastWhiteBalanceTooDark"))

			return false
		}

		const [temperature, tint] = RGBToWhiteBalance(
			editor.photo.whiteBalance.asShotTemperature,
			editor.photo.whiteBalance.asShotTint,
			result.color[0],
			result.color[1],
			result.color[2],
		)

		editor.setAdjustments({
			temperature,
			tint,
		})

		saveCurrentAdjustmentState({
			message: "White Balance Picker",
			changedValue: `${Math.round(temperature)}`,
		})

		return true
	}

	const renderWBModePicker = () => {
		const wbOptions: WbOption[] = ["as-shot", "custom"]
		const wbOptionDisplay = (kind: WbOption) => {
			switch (kind) {
				case "as-shot":
					return t("controls.wbAsShot")
				case "custom":
					return t("controls.wbCustom")
			}
		}

		return (
			<Select
				trackingContext="wb_mode_picker"
				trackingLabel="wb_select"
				aria-label={t("controls.ariaSelectWhiteBalancePreset")}
				value={currentWhiteBalanceSetting}
				disabled={areAdjustmentsPending}
				onValueChange={(value) => {
					handleWBUpdate(value as WbOption)
				}}
			>
				<SelectTrigger aria-label={t("controls.ariaOpenPhotoWhiteBalancePresetList")}>
					{areAdjustmentsPending
						? "Loading"
						: wbOptionDisplay(currentWhiteBalanceSetting)}
				</SelectTrigger>
				<SelectContent aria-label={t("controls.ariaPhotoWhiteBalancePresetList")}>
					<SelectGroup>
						{wbOptions.map((option) => (
							<SelectItem key={option} value={option}>
								{wbOptionDisplay(option)}
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>
		)
	}

	const renderColorProfilePicker = () => {
		const currentColorProfile = editor.renderer.profileManager.getColorProfile(adjustments, {
			isSourceMonochrome,
		})

		const getDisplayName = (kind: ColorProfileName) => {
			switch (kind) {
				case ColorProfileName.Standard:
					return t("controls.colorProfileStandard")
				case ColorProfileName.StandardMonochrome:
					return t("controls.colorProfileStandardBw")
				case ColorProfileName.Color:
					return t("controls.colorProfileColor")
				case ColorProfileName.Monochrome:
					return t("controls.colorProfileMonochrome")
				case ColorProfileName.Embedded: {
					const name = editor.renderer.profileManager.embeddedProfile?.name

					if (name && name === "Apple Embedded Color Profile") {
						return t("controls.colorProfileAppleProraw")
					} else {
						return t("controls.colorProfileCustom")
					}
				}
				default:
					return ""
			}
		}

		const getValue = (value: string) => {
			const colorProfile = parseInt(value)
			if (isNaN(colorProfile)) {
				return ColorProfileName.Color
			}

			return colorProfile
		}

		let allProfiles = [
			ColorProfileName.Standard,
			ColorProfileName.Color,
			ColorProfileName.Monochrome,
		]

		if (editor.renderer.profileManager.embeddedProfile) {
			allProfiles = [ColorProfileName.Embedded, ...allProfiles]
		}

		const selectedProfileName = getDisplayName(currentColorProfile)
		const selectedProfileValue = currentColorProfile.toString()

		const updateColorProfile = (name: string) => {
			if (currentPhoto) {
				const colorProfile = getValue(name)

				editor.setAdjustments({
					colorProfile,
				})

				void saveColorProfileHistory({
					message: "Set Color Profile",
					photo: currentPhoto,
					changedValue: getDisplayName(colorProfile),
				})
			}
		}

		return (
			<Select
				trackingContext="color_profile_picker"
				trackingLabel="color_profile_select"
				aria-label={t("controls.ariaSelectColorProfile")}
				disabled={areAdjustmentsPending}
				value={selectedProfileValue}
				onValueChange={updateColorProfile}
			>
				<SelectTrigger aria-label={t("controls.ariaOpenPhotoColorProfileList")}>
					{areAdjustmentsPending ? "Loading" : selectedProfileName}
				</SelectTrigger>
				<SelectContent aria-label={t("controls.ariaPhotoColorProfileList")}>
					<SelectGroup>
						{allProfiles.map((option) => (
							<SelectItem key={option} value={option.toString()}>
								{getDisplayName(option)}
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>
		)
	}

	const renderLutPanel = () => {
		const hasActiveLut = Boolean(adjustments.userLutId)
		const lutName = hasActiveLut
			? (adjustments.userLutTitle ?? t("controls.untitledLut"))
			: t("controls.lutNone")
		const lutStrength = adjustments.userLutStrength ?? defaultAdjustments.userLutStrength

		const removeLut = () => {
			editor.setAdjustments({
				userLutId: undefined,
				userLutStrength: 1,
				userLutTitle: undefined,
			})

			void saveCurrentAdjustmentState({
				message: "Remove LUT",
			})
		}

		const chooseLutFile = async () => {
			const [file] = await openFilesWithPicker({
				multiple: false,
				types: [
					{
						description: t("controls.cubeLut"),
						accept: {
							"text/plain": [".cube"],
							"application/octet-stream": [".cube"],
						},
					},
				],
			})

			if (!file) {
				return
			}

			try {
				const title = file.name.split(".").slice(0, -1).join(".") || file.name
				const lutAdjustments = await importUserLutFromCUBEFile(file, title)
				editor.setAdjustments(lutAdjustments)

				void saveCurrentAdjustmentState({
					message: "Import LUT",
					changedValue: title,
				})
			} catch (error) {
				console.error("Unable to import LUT", error)
				showToast(t("controls.toastUnableToImportLut"))
			}
		}

		return (
			<ControlGroup name={t("controls.panelLut")} expandKey="lut" disabled={disabled}>
				<div className="flex w-full flex-col pt-1">
					<div className="my-0.5 flex items-center justify-between text-xs text-base-50 opacity-50">
						<span className="mr-2 flex w-[60px] shrink-0 flex-row items-center justify-end capitalize">
							{t("controls.lutActive")}
						</span>
						<span
							className="mr-1 min-w-0 flex-1 truncate text-right text-base-100"
							title={lutName}
							aria-label={t("controls.ariaActiveLutName")}
						>
							{lutName}
						</span>
					</div>
					<Slider
						title={t("controls.sliderStrength")}
						panelName={t("controls.panelLut")}
						focusKey="userLutStrength"
						value={lutStrength}
						defaultValue={defaultAdjustments.userLutStrength}
						min={0}
						max={200}
						step={1}
						keyboardStep={1}
						shiftKeyboardStep={10}
						hoverKeyboardStep={1}
						hoverShiftKeyboardStep={10}
						transform={{
							toRange: (val) => val * 100,
							fromRange: (val) => val / 100,
						}}
						disabled={!hasActiveLut}
						onUpdate={(val) => handleUpdate("userLutStrength", val)}
						onUpdateEnded={(_, displayedValue) =>
							handleUpdateEnded("LUT Strength", displayedValue)
						}
					/>
					<div className="mt-2 flex flex-col gap-2">
						<Button
							trackingLabel="choose_lut_file"
							aria-label={t("controls.ariaChooseLutFile")}
							className="w-full"
							size="sm"
							onClick={() => void chooseLutFile()}
						>
							{t("controls.chooseLutFile")}
						</Button>
						<Button
							trackingLabel="remove_lut"
							aria-label={t("controls.ariaRemoveLut")}
							className="w-full"
							size="sm"
							disabled={!hasActiveLut}
							onClick={removeLut}
						>
							{t("controls.remove")}
						</Button>
					</div>
				</div>
			</ControlGroup>
		)
	}

	const defaultAdjustments = getDefaultAdjustments(currentPhoto)

	const applyBasicAutoAdjustmentsToCurrentPhoto = () => {
		if (currentPhoto) {
			void queueApplyBasicAutoAdjustments(currentPhoto)
		}
	}

	return (
		<div
			aria-label={t("controls.ariaEditorRightPanelGroup")}
			aria-busy={areAdjustmentsPending}
			className="flex flex-col overflow-hidden"
		>
			<ControlGroup
				name={t("controls.panelHistogram")}
				expandKey="histogram"
				isExpanded
				disabled={disabled}
			>
				<div className="flex w-full flex-col pt-1">
					<Histogram />
				</div>
			</ControlGroup>
			<ControlsToolBar />

			<div
				role="region"
				aria-label={t("controls.ariaAdjustmentControls")}
				className="scrollarea flex flex-col overflow-x-hidden"
				ref={ref}
			>
				<ScrollBar visible="hover" />
				<ControlToolsPanel disabled={disabled} adjustments={adjustments} />

				{(activePanel == "edit" || activePanel == "crop") && (
					<>
						<ControlGroup
							name={t("controls.panelBasic")}
							expandKey="basic"
							isExpanded
							disabled={disabled}
							accessoryView={
								isManualMode && (
									<Button
										size="sm"
										trackingLabel={"apply_basic_auto"}
										onClick={(e) => {
											e.stopPropagation()
											void applyBasicAutoAdjustmentsToCurrentPhoto()
										}}
									>
										{t("controls.auto")}
									</Button>
								)
							}
						>
							<div className="ml-1 mr-0 flex flex-row items-center text-xs">
								<div className="ml-auto flex items-center gap-2">
									<span className="whitespace-nowrap">
										{t("controls.colorProfile")}
									</span>
									{renderColorProfilePicker()}
								</div>
							</div>
							<div className="mb-2 ml-1 mr-0 flex flex-row items-center gap-6 text-xs">
								<ColorPickerPipette
									onSelected={handleWBUpdate}
									purpose="white-balance"
								/>
								<div className="ml-auto flex items-center gap-2">
									<span>{t("controls.wb")}</span>
									{renderWBModePicker()}
								</div>
							</div>
							<div className="flex w-full flex-col">
								<Slider
									title={t("controls.sliderTemp")}
									panelName={t("controls.panelBasic")}
									focusKey="temperature"
									background={sliderBackgrounds.temperature}
									defaultValue={editor.whiteBalance.asShotTemperature}
									value={
										adjustments["temperature"] ??
										editor.whiteBalance.asShotTemperature
									}
									min={2000}
									mid={7000}
									max={25000}
									step={1}
									keyboardStep={50}
									shiftKeyboardStep={200}
									plusMinusStep={50}
									shiftPlusMinusStep={200}
									transform={noopSliderTransform}
									onUpdate={(val) => handleUpdate("temperature", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Temperature", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderTint")}
									panelName={t("controls.panelBasic")}
									focusKey="tint"
									background={sliderBackgrounds.tint}
									defaultValue={editor.whiteBalance.asShotTint}
									value={adjustments["tint"] ?? editor.whiteBalance.asShotTint}
									min={-150}
									max={150}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									transform={noopSliderTransform}
									onUpdate={(val) => handleUpdate("tint", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Tint", displayedValue)
									}
								/>
								<div className="my-2 border-b border-base-600/50 dark:border-base-900"></div>
								<Slider
									title={t("controls.sliderExposure")}
									panelName={t("controls.panelBasic")}
									focusKey="exposure"
									value={adjustments["exposure"]}
									transform={noopSliderTransform}
									step={0.05}
									keyboardStep={0.1}
									shiftKeyboardStep={0.33}
									plusMinusStep={0.1}
									shiftPlusMinusStep={0.33}
									min={-5}
									max={5}
									onUpdate={(val) => handleUpdate("exposure", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Exposure", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderContrast")}
									panelName={t("controls.panelBasic")}
									focusKey="contrast"
									background={sliderBackgrounds.contrast}
									value={adjustments["contrast"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("contrast", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Contrast", displayedValue)
									}
								/>
								<div className="my-2 border-b border-base-600/50 dark:border-base-900"></div>
								<Slider
									title={t("controls.sliderHighlights")}
									panelName={t("controls.panelBasic")}
									focusKey="highlights"
									value={adjustments["highlights"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("highlights", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Highlights", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderShadows")}
									panelName={t("controls.panelBasic")}
									focusKey="shadows"
									value={adjustments["shadows"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("shadows", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Shadows", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderWhites")}
									panelName={t("controls.panelBasic")}
									focusKey="whites"
									value={adjustments["whites"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("whites", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Whites", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderBlacks")}
									panelName={t("controls.panelBasic")}
									focusKey="blacks"
									value={adjustments["blacks"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("blacks", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Blacks", displayedValue)
									}
								/>
								<div className="my-2 border-b border-base-600/50 dark:border-base-900"></div>
								<Slider
									title={t("controls.sliderTexture")}
									panelName={t("controls.panelBasic")}
									focusKey="texture"
									value={adjustments["texture"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("texture", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Texture", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderClarity")}
									panelName={t("controls.panelBasic")}
									focusKey="clarity"
									value={adjustments["clarity"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("clarity", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Clarity", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderDehaze")}
									panelName={t("controls.panelBasic")}
									focusKey="dehaze"
									value={adjustments["dehaze"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("dehaze", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Dehaze", displayedValue)
									}
								/>
								<div className="my-2 border-b border-base-600/50 dark:border-base-900"></div>
								<Slider
									title={t("controls.sliderVibrance")}
									panelName={t("controls.panelBasic")}
									disabled={disablesChromaControls}
									focusKey="vibrance"
									background={sliderBackgrounds.saturation}
									value={adjustments["vibrance"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("vibrance", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Vibrance", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderSaturation")}
									panelName={t("controls.panelBasic")}
									disabled={disablesChromaControls}
									focusKey="saturation"
									background={sliderBackgrounds.saturation}
									value={adjustments["saturation"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("saturation", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Saturation", displayedValue)
									}
								/>
							</div>
						</ControlGroup>
						<ControlGroup
							name={t("controls.panelCurves")}
							expandKey="curves"
							disabled={disabled}
						>
							<div className="flex w-full flex-col pt-1">
								<Curves values={adjustments} saveChange={handleUpdateEnded} />
							</div>
						</ControlGroup>
						<ControlGroup
							name={
								disablesChromaControls
									? t("controls.panelBw")
									: t("controls.panelHsl")
							}
							expandKey="hsl"
							disabled={disabled}
						>
							<HSL
								values={adjustments}
								saveChange={handleUpdateEnded}
								isMonochrome={disablesChromaControls}
							/>
						</ControlGroup>
						{!disablesChromaControls && (
							<ControlGroup
								name={t("controls.panelPointColor")}
								expandKey="hslpointcolor"
								disabled={disabled}
							>
								<HSLPointColor
									values={adjustments}
									onAddColor={(pointColor) => {
										editor.setAdjustments({
											pointColors: [
												...editor.adjustments.pointColors,
												pointColor,
											],
										})

										handleUpdateEnded("Added Point Color Swatch")
									}}
									onChangeColor={(pointColor) => {
										const selectedIndex =
											editor.adjustments.pointColors.findIndex(
												(value) => value.id === pointColor.id,
											)

										if (selectedIndex < 0) {
											return
										}

										const pointColors = editor.adjustments.pointColors.slice()
										pointColors[selectedIndex] = pointColor

										editor.setAdjustments({
											pointColors,
										})
									}}
									onEndChangingColor={(_, name, displayValue) => {
										handleUpdateEnded(name, displayValue)
									}}
									onRemoveColor={(pointColor) => {
										editor.setAdjustments({
											pointColors: editor.adjustments.pointColors.filter(
												(color) => color.id !== pointColor.id,
											),
										})

										handleUpdateEnded("Removed Point Color Swatch")
									}}
									onRemoveAllColors={() => {
										editor.setAdjustments({
											pointColors: [],
										})

										handleUpdateEnded("Removed All Point Color Swatches")
									}}
								/>
							</ControlGroup>
						)}
						<ControlGroup
							name={t("controls.panelColorGrading")}
							expandKey="colorGrading"
							disabled={disabled}
						>
							<Grading values={adjustments} saveChange={handleUpdateEnded} />
						</ControlGroup>
						<ControlGroup
							name={t("controls.panelDetail")}
							expandKey="detail"
							disabled={disabled}
						>
							<div className="flex w-full flex-col pt-1">
								<Slider
									title={t("controls.sliderSharpening")}
									panelName={t("controls.panelDetail")}
									focusKey="sharpen"
									defaultValue={defaultAdjustments.sharpen}
									min={0}
									max={150}
									value={adjustments["sharpen"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("sharpen", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Sharpen", displayedValue)
									}
								/>
								<div className="my-2 text-center text-xs">
									{t("controls.noiseReduction")}
								</div>
								<Slider
									title={t("controls.sliderLuminance")}
									panelName={t("controls.panelDetail")}
									focusKey="luminanceNoiseReduction"
									defaultValue={defaultAdjustments.luminanceNoiseReduction}
									min={0}
									value={adjustments["luminanceNoiseReduction"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("luminanceNoiseReduction", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded(
											"Luminance Noise Reduction",
											displayedValue,
										)
									}
								/>
								<Slider
									title={t("controls.sliderColor")}
									panelName={t("controls.panelDetail")}
									focusKey="colorNoiseReduction"
									defaultValue={defaultAdjustments.colorNoiseReduction}
									min={0}
									value={adjustments["colorNoiseReduction"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("colorNoiseReduction", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Color Noise Reduction", displayedValue)
									}
								/>
							</div>
						</ControlGroup>

						{renderLensCorrection()}

						<ControlGroup
							name={t("controls.panelTransform")}
							expandKey="transform"
							disabled={disabled}
						>
							<div className="mt-2 flex justify-end gap-2">
								<Button
									trackingLabel="auto_vertical_transform"
									aria-label={t("controls.ariaAutoVerticalTransform")}
									className="w-fit justify-end"
									size="sm"
									onClick={autoVertical}
								>
									{t("controls.autoVertical")}
								</Button>
							</div>
							<Slider
								title={t("controls.sliderDistortion")}
								panelName={t("controls.panelTransform")}
								focusKey="lensDistortion"
								value={adjustments["lensDistortion"]}
								keyboardStep={1}
								shiftKeyboardStep={10}
								hoverKeyboardStep={5}
								hoverShiftKeyboardStep={20}
								onUpdate={(val) => handleUpdate("lensDistortion", val)}
								onUpdateEnded={(_, displayedValue) =>
									handleUpdateEnded("Distortion", displayedValue)
								}
							/>
							<Slider
								title={t("controls.sliderVertical")}
								panelName={t("controls.panelTransform")}
								focusKey="perspectiveVertical"
								value={adjustments["perspectiveVertical"]}
								keyboardStep={1}
								shiftKeyboardStep={10}
								hoverKeyboardStep={5}
								hoverShiftKeyboardStep={20}
								onUpdate={(val) =>
									handleTransformUpdate("perspectiveVertical", val)
								}
								onUpdateEnded={(_, displayedValue) =>
									handleUpdateEnded("Vertical Perspective", displayedValue)
								}
							/>
							<Slider
								title={t("controls.sliderHorizontal")}
								panelName={t("controls.panelTransform")}
								focusKey="perspectiveHorizontal"
								value={adjustments["perspectiveHorizontal"]}
								keyboardStep={1}
								shiftKeyboardStep={10}
								hoverKeyboardStep={5}
								hoverShiftKeyboardStep={20}
								onUpdate={(val) =>
									handleTransformUpdate("perspectiveHorizontal", val)
								}
								onUpdateEnded={(_, displayedValue) =>
									handleUpdateEnded("Horizontal Perspective", displayedValue)
								}
							/>
							<Slider
								title={t("controls.sliderRotate")}
								panelName={t("controls.panelTransform")}
								focusKey="perspectiveRotate"
								value={adjustments["perspectiveRotate"]}
								step={0.1}
								keyboardStep={0.1}
								shiftKeyboardStep={0.5}
								plusMinusStep={0.1}
								shiftPlusMinusStep={0.5}
								min={-10}
								max={10}
								transform={noopSliderTransform}
								onUpdate={(val) => handleTransformUpdate("perspectiveRotate", val)}
								onUpdateEnded={(_, displayedValue) =>
									handleUpdateEnded("Perspective Rotate", displayedValue)
								}
							/>
							<Slider
								title={t("controls.sliderAspect")}
								panelName={t("controls.panelTransform")}
								focusKey="perspectiveAspect"
								value={adjustments["perspectiveAspect"]}
								keyboardStep={1}
								shiftKeyboardStep={10}
								hoverKeyboardStep={5}
								hoverShiftKeyboardStep={20}
								onUpdate={(val) => handleTransformUpdate("perspectiveAspect", val)}
								onUpdateEnded={(_, displayedValue) =>
									handleUpdateEnded("Perspective Aspect", displayedValue)
								}
							/>
							<Slider
								title={t("controls.sliderScale")}
								panelName={t("controls.panelTransform")}
								focusKey="perspectiveScale"
								value={adjustments["perspectiveScale"]}
								min={50}
								max={150}
								defaultValue={defaultAdjustments.perspectiveScale}
								keyboardStep={1}
								shiftKeyboardStep={10}
								hoverKeyboardStep={5}
								hoverShiftKeyboardStep={20}
								onUpdate={(val) => handleTransformUpdate("perspectiveScale", val)}
								onUpdateEnded={(_, displayedValue) =>
									handleUpdateEnded("Perspective Scale", displayedValue)
								}
							/>
							<Slider
								title={t("controls.sliderXOffset")}
								panelName={t("controls.panelTransform")}
								focusKey="perspectiveOffsetX"
								value={adjustments["perspectiveOffsetX"]}
								min={-100}
								max={100}
								step={0.1}
								keyboardStep={0.1}
								shiftKeyboardStep={10}
								plusMinusStep={0.1}
								shiftPlusMinusStep={10}
								transform={{
									toRange: (val) => val * 100,
									fromRange: (val) => val / 100,
								}}
								onUpdate={(val) => handleTransformUpdate("perspectiveOffsetX", val)}
								onUpdateEnded={(_, displayedValue) =>
									handleUpdateEnded("X Offset", displayedValue)
								}
							/>
							<Slider
								title={t("controls.sliderYOffset")}
								panelName={t("controls.panelTransform")}
								focusKey="perspectiveOffsetY"
								value={adjustments["perspectiveOffsetY"]}
								min={-100}
								max={100}
								step={0.1}
								keyboardStep={0.1}
								shiftKeyboardStep={10}
								plusMinusStep={0.1}
								shiftPlusMinusStep={10}
								transform={{
									toRange: (val) => val * 100,
									fromRange: (val) => val / 100,
								}}
								onUpdate={(val) => handleTransformUpdate("perspectiveOffsetY", val)}
								onUpdateEnded={(_, displayedValue) =>
									handleUpdateEnded("Y Offset", displayedValue)
								}
							/>
						</ControlGroup>
						<ControlGroup
							name={t("controls.panelEffects")}
							expandKey="effects"
							disabled={disabled}
						>
							<div className="flex w-full flex-col pt-1">
								<div className="my-2 text-center text-xs">
									{t("controls.vignette")}
								</div>
								<Slider
									title={t("controls.sliderAmount")}
									panelName={t("controls.panelEffects")}
									sectionName={t("controls.sectionVignetteAmount")}
									focusKey="vignette"
									value={adjustments["vignette"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("vignette", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Vignette", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderMidpoint")}
									panelName={t("controls.panelEffects")}
									sectionName={t("controls.sectionVignetteMidpoint")}
									focusKey="vignetteMidpoint"
									defaultValue={100}
									min={0}
									max={100}
									value={adjustments["vignetteMidpoint"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("vignetteMidpoint", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Vignette Midpoint", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderRoundness")}
									panelName={t("controls.panelEffects")}
									sectionName={t("controls.sectionVignetteRoundness")}
									focusKey="vignetteRoundness"
									defaultValue={0}
									min={-100}
									max={100}
									value={adjustments["vignetteRoundness"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("vignetteRoundness", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Vignette Roundness", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderFeather")}
									panelName={t("controls.panelEffects")}
									sectionName={t("controls.sectionVignetteFeather")}
									focusKey="vignetteFeather"
									defaultValue={50}
									min={0}
									max={100}
									value={adjustments["vignetteFeather"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("vignetteFeather", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Vignette Feather", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderHighlights")}
									panelName={t("controls.panelEffects")}
									sectionName={t("controls.sectionVignetteHighlights")}
									focusKey="vignetteHighlights"
									defaultValue={0}
									min={0}
									max={100}
									value={adjustments["vignetteHighlights"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("vignetteHighlights", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Vignette Highlights", displayedValue)
									}
								/>
								<div className="my-2 text-center text-xs">
									{t("controls.grain")}
								</div>
								<Slider
									title={t("controls.sliderAmount")}
									panelName={t("controls.panelEffects")}
									sectionName={t("controls.sectionGrainAmount")}
									focusKey="grainAmount"
									value={adjustments["grainAmount"]}
									min={0}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("grainAmount", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Grain Amount", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderSize")}
									panelName={t("controls.panelEffects")}
									sectionName={t("controls.sectionGrainSize")}
									focusKey="grainSize"
									defaultValue={defaultAdjustments.grainSize}
									min={0}
									value={adjustments["grainSize"]}
									disabled={adjustments["grainAmount"] === 0}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("grainSize", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Grain Size", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderRoughness")}
									panelName={t("controls.panelEffects")}
									sectionName={t("controls.sectionGrainRoughness")}
									focusKey="grainRoughness"
									min={0}
									defaultValue={defaultAdjustments.grainRoughness}
									value={adjustments["grainRoughness"]}
									disabled={adjustments["grainAmount"] === 0}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("grainRoughness", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Grain Roughness", displayedValue)
									}
								/>
							</div>
						</ControlGroup>
						<ControlGroup
							name={t("controls.panelCalibration")}
							expandKey="calibration"
							disabled={disabled}
						>
							<div className="flex w-full flex-col pt-1">
								<div className="my-2 text-center text-xs">
									{t("controls.redPrimary")}
								</div>
								<Slider
									title={t("controls.sliderHue")}
									panelName={t("controls.panelCalibration")}
									sectionName={t("controls.sectionRedHue")}
									focusKey="calibrationRedHue"
									background={sliderBackgrounds.calibrationRedHue}
									value={adjustments["calibrationRedHue"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("calibrationRedHue", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Calibration Red Hue", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderSaturation")}
									panelName={t("controls.panelCalibration")}
									sectionName={t("controls.sectionRedSaturation")}
									focusKey="calibrationRedSat"
									background={sliderBackgrounds.calibrationRedSat}
									value={adjustments["calibrationRedSat"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("calibrationRedSat", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded(
											"Calibration Red Saturation",
											displayedValue,
										)
									}
								/>
								<div className="my-2 text-center text-xs">
									{t("controls.greenPrimary")}
								</div>
								<Slider
									title={t("controls.sliderHue")}
									panelName={t("controls.panelCalibration")}
									sectionName={t("controls.sectionGreenHue")}
									focusKey="calibrationGreenHue"
									background={sliderBackgrounds.calibrationGreenHue}
									value={adjustments["calibrationGreenHue"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("calibrationGreenHue", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Calibration Green Hue", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderSaturation")}
									panelName={t("controls.panelCalibration")}
									sectionName={t("controls.sectionGreenSaturation")}
									focusKey="calibrationGreenSat"
									background={sliderBackgrounds.calibrationGreenSat}
									value={adjustments["calibrationGreenSat"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("calibrationGreenSat", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded(
											"Calibration Green Saturation",
											displayedValue,
										)
									}
								/>
								<div className="my-2 text-center text-xs">
									{t("controls.bluePrimary")}
								</div>
								<Slider
									title={t("controls.sliderHue")}
									panelName={t("controls.panelCalibration")}
									sectionName={t("controls.sectionBlueHue")}
									focusKey="calibrationBlueHue"
									background={sliderBackgrounds.calibrationBlueHue}
									value={adjustments["calibrationBlueHue"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("calibrationBlueHue", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded("Calibration Blue Hue", displayedValue)
									}
								/>
								<Slider
									title={t("controls.sliderSaturation")}
									panelName={t("controls.panelCalibration")}
									sectionName={t("controls.sectionBlueSaturation")}
									focusKey="calibrationBlueSat"
									background={sliderBackgrounds.calibrationBlueSat}
									value={adjustments["calibrationBlueSat"]}
									keyboardStep={1}
									shiftKeyboardStep={10}
									hoverKeyboardStep={5}
									hoverShiftKeyboardStep={20}
									onUpdate={(val) => handleUpdate("calibrationBlueSat", val)}
									onUpdateEnded={(_, displayedValue) =>
										handleUpdateEnded(
											"Calibration Blue Saturation",
											displayedValue,
										)
									}
								/>
							</div>
						</ControlGroup>
						{renderLutPanel()}
						<ControlGroup
							name={t("controls.panelAiColorMatch")}
							expandKey="color_match"
							isExpanded
							hideSeparator
							onExpandedChange={(expanded) => {
								if (!expanded) {
									setLeftExpandedPanel(null)
								}
							}}
						>
							<Suspense
								fallback={<div className="size-full shrink-0 bg-base-800"></div>}
							>
								<ColorMatchPanel />
							</Suspense>
						</ControlGroup>
					</>
				)}
			</div>
		</div>
	)
}
